import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Icon,
  Stack,
  Typography,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid2,
} from "@mui/material";
import { MathJax } from "better-react-mathjax";
import LoadingButton from "@mui/lab/LoadingButton";

import theme from "components/foundations/themes";
import { Title, Subtitle } from "components/elements/texts";
import CustomCard from "components/modules/cards";
import {
  convertDifficultyToColor,
  convertDifficultyToString,
  QuestionTypes,
  getQuestionTypeDisplay,
} from "utils/questionHelpers";
import { replacePlaceholdersWithImages } from "utils/imageExtraction";
import { sanitizeQuestionHtml } from "utils/sanitizeHtml";


const QuestionReviewForm = ({ 
  title, 
  subtitle, 
  question, 
  skills, 
  onApprove, 
  onReject, 
  isLoading 
}) => {
  if (!question) return null;

  const selectedSkill = skills?.find(skill => skill.id === question.skill);

  return (
    <MathJax>
      <Title value={title} />
      <Subtitle value={subtitle} />
      <br />
      
      <Grid2 container spacing={3}>
        {/* Question Metadata */}
        <Grid2 size={12}>
          <CustomCard>
            <Title 
              value="Question Metadata" 
              color={theme.palette.primary.main} 
            />
            <br />
            
            <Grid2 container spacing={2}>
              <Grid2 size={{xs:12, md:6}}>
                <Typography variant="h6" gutterBottom>
                  Question Type
                </Typography>
                <Chip 
                  label={getQuestionTypeDisplay(question.type)}
                  color="primary"
                  variant="outlined"
                />
              </Grid2>
              
              <Grid2 size={{xs:12, md:6}}>
                <Typography variant="h6" gutterBottom>
                  Skill
                </Typography>
                <Chip 
                  label={selectedSkill?.name || 'Unknown Skill'}
                  variant="outlined"
                />
              </Grid2>
              
              <Grid2 size={{xs:12, md:6}}>
                <Typography variant="h6" gutterBottom>
                  Difficulty
                </Typography>
                <Chip
                  label={convertDifficultyToString(question.difficulty)}
                  sx={{
                    color: "white",
                    backgroundColor: convertDifficultyToColor(question.difficulty),
                  }}
                />
              </Grid2>
              
              <Grid2 size={{xs:12, md:6}}>
                <Typography variant="h6" gutterBottom>
                  Time Limit
                </Typography>
                <Chip 
                  label={`${question.time} seconds`}
                  icon={<Icon>timer</Icon>}
                  variant="outlined"
                />
              </Grid2>
            </Grid2>
          </CustomCard>
        </Grid2>

        {/* Question Statement */}
        <Grid2 size={12}>
          <CustomCard>
            <Title
              value="Question Statement"
              color={theme.palette.primary.main}
            />
            <br />
            <Box sx={{ minHeight: '100px' }}>
              <MathJax>
                <div dangerouslySetInnerHTML={{
                  __html: sanitizeQuestionHtml(
                    replacePlaceholdersWithImages(
                      question.statement,
                      question.resources || []
                    )
                  )
                }} />
              </MathJax>
            </Box>
          </CustomCard>
        </Grid2>

        {/* Answer Choices (for non-coding questions) */}
        {question.type !== QuestionTypes.CODING && question.type !== 2 && question.choices && (
          <Grid2 size={12}>
            <CustomCard>
              <Title 
                value="Answer Choices" 
                color={theme.palette.primary.main} 
              />
              <br />
              
              {question.choices.map((choice, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Icon
                      sx={{
                        color: choice.is_correct_answer
                          ? theme.palette.success.main
                          : theme.palette.text.secondary
                      }}
                    >
                      {choice.is_correct_answer ? "check_circle" : "radio_button_unchecked"}
                    </Icon>

                    <Box sx={{ flexGrow: 1 }}>
                      <MathJax>
                        <div dangerouslySetInnerHTML={{
                          __html: sanitizeQuestionHtml(
                            replacePlaceholdersWithImages(
                              choice.text,
                              question.resources || []
                            )
                          )
                        }} />
                      </MathJax>
                    </Box>

                    {choice.is_correct_answer && (
                      <Chip
                        label="Correct Answer"
                        color="success"
                        size="small"
                      />
                    )}
                  </Stack>
                </Box>
              ))}
            </CustomCard>
          </Grid2>
        )}

        {/* Coding Question Details */}
        {(question.type === QuestionTypes.CODING || question.type === 2) && (
          <>
            {/* Coding Settings */}
            <Grid2 size={12}>
              <CustomCard>
                <Title 
                  value="Coding Question Settings" 
                  color={theme.palette.primary.main} 
                />
                <br />
                
                <Grid2 container spacing={3}>
                  <Grid2 size={{xs:12, md:6}}>
                    <Typography variant="h6" gutterBottom>
                      Allowed Languages
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {question.allowedLanguages && question.allowedLanguages.length > 0 ? (
                        question.allowedLanguages.map((lang, index) => (
                          <Chip key={index} label={lang} variant="outlined" />
                        ))
                      ) : question.codingLanguages && question.codingLanguages.length > 0 ? (
                        question.codingLanguages.map((langObj, index) => (
                          <Chip key={index} label={langObj.language} variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No languages specified</Typography>
                      )}
                    </Stack>
                  </Grid2>

                  <Grid2 size={{xs:12, md:6}}>
                    <Typography variant="h6" gutterBottom>
                      Solution Code Language
                    </Typography>
                    <Chip 
                      label={question.solutionCodeLanguage || question.codeMode || "Not specified"}
                      color="primary"
                      variant="outlined"
                    />
                  </Grid2>

                  <Grid2 size={{xs:12, md:6}}>
                    <Typography variant="h6" gutterBottom>
                      Settings
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        Allow Running Test Cases: {question.allowRunningTestCases ? "Yes" : "No"}
                      </Typography>
                      <Typography variant="body2">
                        Inform Test Case Correctness: {question.informsTestCaseCorrectness ? "Yes" : "No"}
                      </Typography>
                    </Stack>
                  </Grid2>

                  <Grid2 size={{xs:12, md:6}}>
                    <Typography variant="h6" gutterBottom>
                      Output Configuration
                    </Typography>
                    <Typography variant="body2">
                      Lines per Test Case: {question.outputLinesPerTestCase || 1}
                    </Typography>
                  </Grid2>
                </Grid2>
              </CustomCard>
            </Grid2>

            {/* Custom Code Wrapper */}
            {question.customCodeWrapper && (
              <Grid2 size={12}>
                <CustomCard>
                  <Title 
                    value="Custom Code Wrapper" 
                    color={theme.palette.primary.main} 
                  />
                  <br />
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      backgroundColor: theme.palette.grey[50],
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      overflow: 'auto'
                    }}
                  >
                    {question.customCodeWrapper}
                  </Paper>
                </CustomCard>
              </Grid2>
            )}

            {/* Solution Code */}
            {question.solutionCode && (
              <Grid2 size={12}>
                <CustomCard>
                  <Title 
                    value="Solution Code" 
                    color={theme.palette.primary.main} 
                  />
                  <br />
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      backgroundColor: theme.palette.grey[50],
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      overflow: 'auto'
                    }}
                  >
                    {question.solutionCode}
                  </Paper>
                </CustomCard>
              </Grid2>
            )}

            {/* Test Cases */}
            {question.testCases && question.testCases.length > 0 && (
              <Grid2 size={12}>
                <CustomCard>
                  <Title 
                    value={`Test Cases (${question.testCases.length})`}
                    color={theme.palette.primary.main} 
                  />
                  <br />
                  
                  <TableContainer sx={{ maxHeight: 440 }} component={Paper} elevation={1}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>#</strong></TableCell>
                          <TableCell><strong>Input (stdin)</strong></TableCell>
                          <TableCell><strong>Expected Output</strong></TableCell>
                          <TableCell><strong>Weight</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {question.testCases.map((testCase, index) => (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell sx={{verticalAlign: "top"}}>
                              <Paper 
                                elevation={0}
                                sx={{ 
                                  p: 1, 
                                  backgroundColor: theme.palette.grey[50],
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  whiteSpace: 'pre-wrap',
                                  maxWidth: 200,
                                  overflow: 'auto'
                                }}
                              >
                                {testCase.stdin || '(empty)'}
                              </Paper>
                            </TableCell>
                            <TableCell sx={{verticalAlign: "top"}}>
                              <Paper 
                                elevation={0}
                                sx={{ 
                                  p: 1, 
                                  backgroundColor: theme.palette.grey[50],
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  whiteSpace: 'pre-wrap',
                                  maxWidth: 200,
                                  overflow: 'auto'
                                }}
                              >
                                {testCase.expected_output || testCase.expectedOutput || '(empty)'}
                              </Paper>
                            </TableCell>
                            <TableCell sx={{verticalAlign: "top"}}>
                              <Chip 
                                label={testCase.weight || 1} 
                                size="small" 
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CustomCard>
              </Grid2>
            )}

            {/* Code Editors in Statement */}
            {question.codeEditors && question.codeEditors.length > 0 && (
              <Grid2 size={12}>
                <CustomCard>
                  <Title 
                    value={`Code Editors in Statement (${question.codeEditors.length})`}
                    color={theme.palette.primary.main} 
                  />
                  <br />
                  
                  {question.codeEditors.map((editor, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Editor {index + 1} - {editor.language || 'Unknown Language'}
                      </Typography>
                      
                      <Grid2 container spacing={2} sx={{ mb: 2 }}>
                        <Grid2>
                          <Chip 
                            label={`Allow Edit: ${editor.allow_edit ? 'Yes' : 'No'}`}
                            color={editor.allow_edit ? 'success' : 'default'}
                            size="small"
                          />
                        </Grid2>
                        <Grid2>
                          <Chip 
                            label={`Allow Copy: ${editor.allow_copy ? 'Yes' : 'No'}`}
                            color={editor.allow_copy ? 'success' : 'default'}
                            size="small"
                          />
                        </Grid2>
                        <Grid2>
                          <Chip 
                            label={`Allow Paste: ${editor.allow_paste ? 'Yes' : 'No'}`}
                            color={editor.allow_paste ? 'success' : 'default'}
                            size="small"
                          />
                        </Grid2>
                      </Grid2>
                      
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 2, 
                          backgroundColor: theme.palette.grey[50],
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          overflow: 'auto'
                        }}
                      >
                        {editor.code || '(empty)'}
                      </Paper>
                      
                      {index < question.codeEditors.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
                </CustomCard>
              </Grid2>
            )}
          </>
        )}
      </Grid2>

      <br />
      
      {/* Approval Actions */}
      <CustomCard>
        <Stack direction="row" justifyContent="center" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            onClick={onReject}
            disabled={isLoading}
            sx={{ textTransform: "none", minWidth: 120 }}
          >
            Reject
          </Button>
          
          <LoadingButton
            variant="contained"
            color="success"
            onClick={onApprove}
            loading={isLoading}
            sx={{ textTransform: "none", minWidth: 120 }}
          >
            Approve
          </LoadingButton>
        </Stack>
      </CustomCard>
    </MathJax>
  );
};

QuestionReviewForm.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  question: PropTypes.object,
  skills: PropTypes.array,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default QuestionReviewForm;
